import { useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { CalendarCheck, ChevronDown, Clock, MapPin, Users } from "lucide-react";

import { PageHero, Prose } from "@/components/site/Page";
import { VisitForm } from "@/components/site/VisitForm";
import { absoluteUrl, seoLinks, ogImage } from "@/lib/seo";
import { cn } from "@/lib/utils";

const TITLE = "Boka ett besök på skolan | Swedish International School";
const DESCRIPTION =
  "Kom och se den svenska skolan i Cancelada mellan Estepona och Marbella. Boka ett besök på plats eller ett digitalt möte – helt utan bindning.";

export const Route = createFileRoute("/boka-besok")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:type", content: "article" },
      { property: "og:url", content: absoluteUrl("/boka-besok") },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:locale", content: "sv_SE" },
      ...ogImage(),
    ],
    links: seoLinks("/boka-besok"),
  }),
  component: BokaBesok,
});

const POINTS = [
  {
    icon: Clock,
    title: "Cirka 45 minuter",
    text: "Rundvandring i lokaler, klassrum och utegård – gärna under skoldagen så ni ser verksamheten igång.",
  },
  {
    icon: Users,
    title: "Ni möter personalen",
    text: "Rektor eller en lärare berättar om läroplanen, språkupplägget och hur en vanlig dag ser ut.",
  },
  {
    icon: MapPin,
    title: "På plats eller digitalt",
    text: "Bor ni fortfarande i Sverige? Vi tar gärna ett videomöte i stället och visar skolan på skärm.",
  },
];

function BokaBesok() {
  return (
    <>
      <PageHero
        eyebrow="Besök oss"
        title="Boka ett besök på skolan"
        intro="Det här är för er som vill se skolan innan ni ansöker. Har ni redan skickat in en ansökan hör skolan av sig och bokar besök och nästa steg – då behöver ni inte boka här. Ett besök är kostnadsfritt och innebär ingen ansökan."
      />

      <section className="mx-auto max-w-6xl px-5 py-14">
        <div className="grid gap-4 sm:grid-cols-3">
          {POINTS.map((p) => (
            <div key={p.title} className="rounded-xl border border-border bg-card p-5 shadow-soft">
              <p.icon className="size-5 text-primary" aria-hidden="true" />
              <h2 className="mt-3 font-display text-base font-semibold">{p.title}</h2>
              <p className="mt-1 text-sm leading-relaxed text-muted-foreground">{p.text}</p>
            </div>
          ))}
        </div>

        <div className="mt-10 grid items-start gap-8 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
          <VisitForm locale="sv" />

          <div className="rounded-xl border border-border bg-sand p-6 shadow-soft">
            <h2 className="flex items-center gap-2 font-display text-lg font-semibold">
              <CalendarCheck className="size-5 text-primary" aria-hidden="true" />
              Redo att söka plats?
            </h2>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
              Ansökningar tas emot löpande och det går bra att börja mitt i terminen. Ansökan är
              inte bindande – ni får besked om plats innan något bekräftas.
            </p>
            <div className="mt-5 flex flex-col gap-3 sm:flex-row">
              <Link
                to="/ansokan"
                className="inline-flex min-h-11 items-center justify-center rounded-md bg-primary px-5 text-sm font-medium text-primary-foreground transition-opacity hover:opacity-90"
              >
                Gör en ansökan
              </Link>
              <Link
                to="/avgifter"
                className="inline-flex min-h-11 items-center justify-center rounded-md border border-border bg-card px-5 text-sm font-medium transition-colors hover:bg-background"
              >
                Se avgifter
              </Link>
            </div>
            <p className="mt-5 text-sm text-muted-foreground">
              Frågor först? Ring +34 607 148 331 (telefontid 15:00–16:00 helgfria vardagar) eller
              mejla{" "}
              <a className="text-primary link-quiet" href="mailto:info@sweintschool.com">
                info@sweintschool.com
              </a>
              .
            </p>
          </div>
        </div>
      </section>

      <Prose>
        <h2>Så går besöket till</h2>
        <p>
          Ni hör av er via formuläret eller telefon, vi svarar per mejl eller ringer upp och
          föreslår ett par tider. På plats möter ni någon ur personalen, får se lokalerna och kan
          ställa alla frågor om läroplan, språk, fritids och avgifter. Många familjer tar med barnen
          – det brukar göra beslutet lättare för alla.
        </p>

        <h2>Vanliga frågor inför besöket</h2>
        <VisitFaq />

        <h2>Hitta hit</h2>
        <p>
          Skolan ligger på Urb Costalita 1 i Cancelada, mellan Estepona och Marbella, några minuter
          från A-7. Vägbeskrivning och karta finns på{" "}
          <Link className="text-primary link-quiet" to="/kontakt">
            kontaktsidan
          </Link>
          .
        </p>
      </Prose>
    </>
  );
}

const VISIT_FAQ = [
  {
    q: "Behöver vi ha bestämt oss?",
    a: ["Nej. Ett besök är just till för att hjälpa er att landa i ett beslut – det är kostnadsfritt och innebär ingen ansökan eller bindning."],
  },
  {
    q: "Kan vi besöka er innan vi har flyttat?",
    a: ["Ja, både på plats under en resa hit och digitalt via videomöte. Välj det som passar er i formuläret."],
  },
  {
    q: "Kan barnet prova en dag?",
    a: ["Det löser vi ofta – berätta i formuläret så återkommer vi med ett förslag."],
  },
];

function VisitFaq() {
  const [open, setOpen] = useState<number | null>(0);
  return (
    <div className="not-prose">
      <div className="divide-y divide-border overflow-hidden rounded-xl border border-border bg-card shadow-soft">
        {VISIT_FAQ.map((item, i) => {
          const isOpen = open === i;
          return (
            <section key={item.q}>
              <h3>
                <button
                  type="button"
                  aria-expanded={isOpen}
                  onClick={() => setOpen(isOpen ? null : i)}
                  className="flex w-full items-start justify-between gap-4 px-5 py-4 text-left transition-colors hover:bg-sand/60"
                >
                  <span className="font-display text-base font-semibold">{item.q}</span>
                  <ChevronDown
                    aria-hidden="true"
                    className={cn(
                      "mt-1 size-4 shrink-0 text-muted-foreground transition-transform duration-200",
                      isOpen && "rotate-180 text-primary",
                    )}
                  />
                </button>
              </h3>
              {isOpen ? (
                <div className="space-y-3 px-5 pb-5 text-[0.95rem] leading-relaxed text-foreground/90">
                  {item.a.map((p, j) => (
                    <p key={j}>{p}</p>
                  ))}
                </div>
              ) : null}
            </section>
          );
        })}
      </div>
      <p className="mt-4 text-sm text-muted-foreground">
        Fler svar finns under{" "}
        <Link className="text-primary link-quiet" to="/fragor-och-svar">
          frågor och svar
        </Link>{" "}
        och praktisk information under{" "}
        <Link className="text-primary link-quiet" to="/avgifter">
          avgifter
        </Link>
        .
      </p>
    </div>
  );
}
